import { useState, forwardRef, Ref, ReactElement, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Slide,
  Grid,
  Button
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { ServicePriceServices } from "src/services/price/servicePriceServices";
import CarsService from "src/services/cars/carsServices";

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface PriceDialogProps {
  open: boolean;
  onClose: () => void;
}

function PriceDialog({ open, onClose }: PriceDialogProps) {
  const [data, setData] = useState<any[]>([]);
  const [carList, setCarList] = useState<any[]>([]);

  const [cityId, setCityId] = useState<number | "">("");
  const [serviceId, setServiceId] = useState<number | "">("");
  const [selectedCar, setSelectedCar] = useState<any>(null);

  const [price, setPrice] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchAllData();
      fetchCars();
    }
  }, [open]);

  const fetchAllData = async () => {
    try {
      const res = await ServicePriceServices.getAllData();
      setData(res);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCars = async () => {
    try {
      const res = await CarsService.getAllCars();
      setCarList(res);
    } catch (error) {
      console.error(error);
    }
  };

  const cities = Array.from(
    new Map(data.map((d) => [d.cityId, d])).values()
  );

  const services = data.filter((d) => d.cityId === cityId);

  useEffect(() => {
    if (cityId && serviceId && selectedCar) {
      const carCategoryId = selectedCar?.carCategory?.id;

      if (carCategoryId) {
        fetchPrice(
          Number(cityId),
          Number(serviceId),
          Number(carCategoryId)
        );
      }
    }
  }, [cityId, serviceId, selectedCar]);

  const fetchPrice = async (
    cityId: number,
    serviceId: number,
    carCategoryId: number
  ) => {
    try {
      const res =
        await ServicePriceServices.getAllServicePricesCityIdServiceIdCarcategoryId(
          cityId,
          serviceId,
          carCategoryId
        );

      const amount =
        res?.[0]?.prices?.find(
          (p: any) => p.carCategoryId === carCategoryId
        )?.amount ?? null;

      setPrice(amount);
    } catch (error) {
      console.error(error);
      setPrice(null);
    }
  };

  const handleCityChange = (e: any) => {
    setCityId(e.target.value);
    setServiceId("");
    setSelectedCar(null);
    setPrice(null);
  };

  const handleServiceChange = (e: any) => {
    setServiceId(e.target.value);
    setSelectedCar(null);
    setPrice(null);
  };

  const handleCarChange = (e: any) => {
    const car = carList.find((c) => c.id === e.target.value);
    setSelectedCar(car);
  };

  const handleClose = () => {
    onClose();
    setCityId("");
    setServiceId("");
    setSelectedCar(null);
    setPrice(null);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
    >
      <DialogTitle>Check Service Price</DialogTitle>

      <DialogContent>
        <Grid container spacing={3} mt={1}>

          <Grid item xs={12}>
            <TextField
              select
              label="Select City"
              value={cityId}
              onChange={handleCityChange}
              fullWidth
            >
              {cities.map((item: any) => (
                <MenuItem key={item.cityId} value={item.cityId}>
                  {item.cityName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Select Service"
              value={serviceId}
              onChange={handleServiceChange}
              disabled={!cityId}
              fullWidth
            >
              {services.map((item: any) => (
                <MenuItem key={item.itemId} value={item.itemId}>
                  {item.itemName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Select Car"
              value={selectedCar?.id || ""}
              onChange={handleCarChange}
              disabled={!serviceId}
              fullWidth
            >
              {carList.map((car: any) => (
                <MenuItem key={car.id} value={car.id}>
                  {car.name} ({car.brand?.name})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Price"
              value={price !== null ? `₹ ${price}` : ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PriceDialog;